import { FC } from "react";
import { useTrans } from "../../../hooks";
import { Text, HStack, Flex, Tag, SimpleGrid, Box } from "@chakra-ui/react";
import {
    DataType,
    IKaTableProps,
    ITableInstance,
    Table as KaTable,
    SortingMode,
} from "ka-table";
import { Column, PagingOptions } from "ka-table/models";
import { ICellTextProps } from "ka-table/props";
import {
    AppOptionBoxItem,
    AppOptionsBox,
    KaPageSizeSelector,
} from "../../../components";
import { APPC, UTIL } from "../../../config";
import { useAuthData } from "../../../contexts";

export const MyInternshipList: FC<{
    table: ITableInstance;
    paging: PagingOptions;
    list: any[];
    loadingList?: boolean;
    onActionClick?: (key: string, data?: any) => void;
}> = ({
    table,
    paging,
    list,
    loadingList = false,
    onActionClick = () => {},
}) => {
    const { t } = useTrans();
    const { grant } = useAuthData();

    const dayjs = UTIL.getDayJs();
    const columns: Column[] = [
        {
            key: "internshipSchedule.startDate",
            title: t("padm.InternshipPage:text.pageTitle"),
            dataType: DataType.String,
        },
        {
            key: "schedule",
            title: t("padm.MyInternshipPage:text.schedule"),
            dataType: DataType.String,
            isSortable: false,
        },
        {
            key: "action",
            title: "",
            style: { textAlign: "right" },
            dataType: DataType.Object,
            width: 50,
            isSortable: false,
        },
    ];

    const actionOptions: AppOptionBoxItem[] = [
        {
            key: "detail",
            label: t("cmn:option.detail"),
            iconName: "icl-eye",
        },
    ];

    if (grant.isStudent) {
        actionOptions.unshift({
            key: "timesheet-report",
            label: t("padm.MyInternshipPage:text.option.timesheetReport"),
            iconName: "icl-file-pdf",
        } as AppOptionBoxItem);
        actionOptions.unshift({
            key: "timesheet",
            label: t("padm.MyInternshipPage:text.option.timesheet"),
            iconName: "icl-spreadsheet",
        } as AppOptionBoxItem);
        actionOptions.unshift({
            key: "skills",
            label: t("padm.InternshipScheduleStudentPage:text.option.skills"),
            iconName: "icl-lightbulb",
        } as AppOptionBoxItem);
    } else {
        actionOptions.unshift({
            key: "assignStudents",
            label: t("padm.MyInternshipPage:text.option.assignStudents"),
            iconName: "icl-user-plus",
        } as AppOptionBoxItem);
        actionOptions.unshift({
            key: "students",
            label: t("padm.InternshipSchedulePage:text.option.students"),
            iconName: "icl-user",
        } as AppOptionBoxItem);
    }

    const kaTableProps: IKaTableProps = {
        table: table,
        columns: columns,
        data: list || [],
        loading: {
            enabled: loadingList,
        },
        paging: paging,
        rowKeyField: "id",
        sortingMode: SortingMode.SingleRemote,
        childComponents: {
            pagingSizes: {
                content: (props) => <KaPageSizeSelector {...props} />,
            },
            cellText: {
                content: (props: ICellTextProps) => {
                    switch (props?.column?.key) {
                        case "internshipSchedule.startDate":
                            return (
                                <Flex flexDir={"column"} gap={2}>
                                    <Flex flexDir={"column"} gap={1}>
                                        <Text
                                            textStyle={"mediumXs"}
                                            textDecoration={"underline"}
                                        >
                                            {t("ent.Company:name.label")}
                                        </Text>
                                        <Text
                                            textStyle={"mediumMd"}
                                            color={"var(--chakra-colors-black)"}
                                        >
                                            {
                                                props?.rowData
                                                    ?.internshipSchedule
                                                    ?.internship?.company?.name
                                            }
                                        </Text>
                                    </Flex>
                                    <Flex flexDir={"column"} gap={1}>
                                        <Text
                                            textStyle={"mediumXs"}
                                            textDecoration={"underline"}
                                        >
                                            {t("ent.Internship:name.label")}
                                        </Text>
                                        <Text
                                            textStyle={"mediumMd"}
                                            color={"var(--chakra-colors-black)"}
                                        >
                                            {`${
                                                props?.rowData
                                                    ?.internshipSchedule
                                                    ?.internship?.name
                                            } (${
                                                props?.rowData
                                                    ?.internshipSchedule?.name
                                            })`}
                                        </Text>
                                    </Flex>
                                    <SimpleGrid
                                        columns={{ lg: 1, xl: 2 }}
                                        spacingX={2}
                                        spacingY={2}
                                    >
                                        <Flex flexDir={"column"} gap={1}>
                                            <Text
                                                textStyle={"mediumXs"}
                                                textDecoration={"underline"}
                                            >
                                                {t(
                                                    "ent.InternshipSchedule:trainingYear.label"
                                                )}
                                            </Text>
                                            <Flex gap={3}>
                                                <Tag
                                                    size={"sm"}
                                                    bg={
                                                        "var(--chakra-colors-primaryT80)"
                                                    }
                                                >
                                                    <Box
                                                        display="inline-block"
                                                        w="12px"
                                                        h="12px"
                                                        mr="2"
                                                        bg={
                                                            props?.rowData
                                                                ?.internshipSchedule
                                                                ?.trainingYear
                                                                ?.color
                                                        }
                                                        borderRadius={"full"}
                                                    ></Box>
                                                    {`${
                                                        props?.rowData
                                                            ?.internshipSchedule
                                                            ?.trainingYear?.code
                                                    } - ${
                                                        props?.rowData
                                                            ?.internshipSchedule
                                                            ?.trainingYear?.year
                                                    }`}
                                                </Tag>
                                            </Flex>
                                        </Flex>
                                        {!grant.isStudent && (
                                            <Flex flexDir={"column"} gap={1}>
                                                <Text
                                                    textStyle={"mediumXs"}
                                                    textDecoration={"underline"}
                                                >
                                                    {t(
                                                        "ent.InternshipScheduleSeat:noOfSeats.label"
                                                    )}
                                                </Text>
                                                <Text
                                                    textStyle={"mediumMd"}
                                                    color={
                                                        "var(--chakra-colors-black)"
                                                    }
                                                >
                                                    {`${props?.rowData?.noOfAssignedSeats || 0} / ${props?.rowData?.noOfSeats}`}
                                                </Text>
                                            </Flex>
                                        )}
                                    </SimpleGrid>
                                </Flex>
                            );
                        case "schedule":
                            return (
                                <Flex flexDir={"column"} gap={2}>
                                    <HStack>
                                        <Text
                                            minW={"80px"}
                                            textStyle={"mediumSm"}
                                            textDecoration={"underline"}
                                        >
                                            {t("cmn:label.date")}
                                        </Text>
                                        <Text
                                            textStyle={"mediumSm"}
                                            color={"var(--chakra-colors-black)"}
                                        >
                                            {`${dayjs(
                                                props?.rowData
                                                    ?.internshipSchedule
                                                    ?.startDate
                                            ).format(APPC.APP_DATE_FORMAT)} -   
                                                                ${dayjs(
                                                                    props
                                                                        ?.rowData
                                                                        ?.internshipSchedule
                                                                        ?.endDate
                                                                ).format(
                                                                    APPC.APP_DATE_FORMAT
                                                                )}`}
                                        </Text>
                                    </HStack>
                                    {!props?.rowData?.internshipSchedule
                                        ?.isScheduleDetail && (
                                        <>
                                            <HStack>
                                                <Text
                                                    minW={"80px"}
                                                    textStyle={"mediumSm"}
                                                    textDecoration={"underline"}
                                                >
                                                    {t("cmn:label.time")}
                                                </Text>
                                                <Text
                                                    textStyle={"mediumSm"}
                                                    color={
                                                        "var(--chakra-colors-black)"
                                                    }
                                                >
                                                    {`${dayjs
                                                        .utc(
                                                            props?.rowData
                                                                ?.internshipSchedule
                                                                ?.startTime
                                                        )
                                                        .format(
                                                            APPC.APP_TIME_FORMAT
                                                        )} -
                                                                ${dayjs
                                                                    .utc(
                                                                        props
                                                                            ?.rowData
                                                                            ?.internshipSchedule
                                                                            ?.endTime
                                                                    )
                                                                    .format(
                                                                        APPC.APP_TIME_FORMAT
                                                                    )}`}
                                                </Text>
                                            </HStack>
                                            <HStack>
                                                <Text
                                                    minW={"80px"}
                                                    textStyle={"mediumSm"}
                                                    textDecoration={"underline"}
                                                >
                                                    {t(
                                                        "ent.InternshipSchedule:breakDuration.label"
                                                    )}
                                                </Text>
                                                <Text
                                                    textStyle={"mediumSm"}
                                                    color={
                                                        "var(--chakra-colors-black)"
                                                    }
                                                >
                                                    {
                                                        props?.rowData
                                                            ?.internshipSchedule
                                                            ?.breakDuration
                                                    }{" "}
                                                    {t("cmn:label.min")}
                                                </Text>
                                            </HStack>
                                            <HStack>
                                                <Text
                                                    minW={"80px"}
                                                    textStyle={"mediumSm"}
                                                    textDecoration={"underline"}
                                                >
                                                    {t(
                                                        "ent.InternshipSchedule:weekDays.label"
                                                    )}
                                                </Text>
                                                <Flex gap={2}>
                                                    {Object.keys(
                                                        props?.rowData
                                                            ?.internshipSchedule
                                                            ?.weekDays || {}
                                                    ).map((day) => (
                                                        <Tag
                                                            key={day}
                                                            w={"40px"}
                                                            size={"sm"}
                                                            justifyContent={
                                                                "center"
                                                            }
                                                            bg={
                                                                props?.rowData
                                                                    ?.internshipSchedule
                                                                    ?.weekDays?.[
                                                                    day
                                                                ]
                                                                    ? "var(--chakra-colors-primary)"
                                                                    : "var(--chakra-colors-greyT95)"
                                                            }
                                                            color={
                                                                props?.rowData
                                                                    ?.internshipSchedule
                                                                    ?.weekDays?.[
                                                                    day
                                                                ]
                                                                    ? "var(--chakra-colors-white)"
                                                                    : "var(--chakra-colors-contentT70)"
                                                            }
                                                        >
                                                            {day
                                                                .charAt(0)
                                                                .toUpperCase() +
                                                                day.slice(1)}
                                                        </Tag>
                                                    ))}
                                                </Flex>
                                            </HStack>
                                        </>
                                    )}
                                    <HStack>
                                        <Text
                                            minW={"80px"}
                                            textStyle={"mediumSm"}
                                            textDecoration={"underline"}
                                        >
                                            {t(
                                                "ent.InternshipSchedule:totalTime.label"
                                            )}
                                        </Text>
                                        <Text
                                            textStyle={"mediumSm"}
                                            color={"var(--chakra-colors-black)"}
                                        >
                                            {`${
                                                props?.rowData
                                                    ?.internshipSchedule
                                                    ?.totalTime / 60
                                            } ${t("cmn:label.hours")}`}
                                        </Text>
                                    </HStack>
                                </Flex>
                            );
                        case "action":
                            return (
                                <HStack justify={"flex-end"} gap={3}>
                                    <AppOptionsBox
                                        options={actionOptions}
                                        data={props?.rowData}
                                        onClick={onActionClick}
                                    />
                                </HStack>
                            );
                    }
                },
            },
        },
    };

    return <KaTable {...kaTableProps} />;
};
